<template>
  <v-menu v-model="dropdownIsOpen" :close-on-content-click="false" location="bottom center" transition="slide-y-transition" offset="4"
          :max-width="500" :width="selectedSection ? 500 : undefined">
    <template #activator="{ props }">
      <v-btn variant="text" icon size="small" v-bind="props">
        <v-badge :model-value="!! unseenNotifications.length" :content="unseenNotifications.length" color="errorred" offset-x="-2" offset-y="-4">
          <v-icon>far fa-bell</v-icon>
        </v-badge>
        <v-tooltip :disabled="dropdownIsOpen" location="bottom" activator="parent">{{ $t('NOTIFICATIONS.NOTIFICATIONS') }}</v-tooltip>
      </v-btn>
    </template>

    <!-- list of sections with unseen notifications -->
    <v-list v-if="! selectedSection" density="comfortable" class="py-0">
      <v-list-subheader style="position: relative">
        <div class="flex-center-center">
          {{ $t('NOTIFICATIONS.NOTIFICATIONS_COUNT', unseenNotifications.length) }}
          <v-tooltip location="bottom" max-width="200">
            <template #activator="{ props }">
              <v-icon color="info" style="position: absolute; right: 12px" v-bind="props">far fa-question-circle</v-icon>
            </template>
            <span>{{ $t('NOTIFICATIONS.FIND_HERE_NOTIFICATIONS') }}</span>
          </v-tooltip>
        </div>
      </v-list-subheader>
      <v-list-item v-for="sectionNotif in unseenNotificationsCountBySection" :key="`${sectionNotif.section_type}|${sectionNotif.section_id}`"
                   :title="sectionNotif.section_type == 'planning' ? projectsMap.get(sectionNotif.section_id)?.title || '...' : null"
                   :subtitle="$t('NOTIFICATIONS.NOTIFICATIONS_COUNT', sectionNotif.count)"
                   @click="selectedSection = sectionNotif">
        <template #prepend>
          <v-img v-if="sectionNotif.section_type == 'planning'" :src="projectsMap.get(sectionNotif.section_id)?.imgurl" :width="48" :max-height="40" cover position="0 0" class="mr-2"></v-img>
          <v-icon v-else size="small" start>far fa-check-square</v-icon>
        </template>
        <template #append>
          <v-btn-icon :to="notifSectionUrl(sectionNotif)" size="small" class="ml-2 mr-n2" @click.stop><v-icon>far fa-external-link</v-icon></v-btn-icon>
        </template>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item to="/manage#profile-notifications" :active="false">
        <div class="d-flex align-center">
          <v-icon size="small" start>far fa-cog</v-icon>
          {{ $t('ACCOUNT.CONFIGURE_EMAILS') }}
        </div>
      </v-list-item>
    </v-list>

    <!-- selectedSection notifications -->
    <v-list v-else density="compact">
      <v-list-subheader style="padding-inline-start: 4px !important; position: relative">
        <div class="flex-center-center">
          <v-btn-icon @click="selectedSection = null" size="small"><v-icon>far fa-chevron-left</v-icon></v-btn-icon>
          {{ selectedSectionTitle }}
          <v-btn-icon v-if="hasUnseenNotifications" :title="$t('NOTIFICATIONS.MARK_ALL_AS_READ')" size="small" color="successgreen" style="position: absolute; right: 0" @click="seenAllNotifications">
            <v-icon>fas fa-check-circle</v-icon>
          </v-btn-icon>
        </div>
      </v-list-subheader>
      <v-list-subheader v-if="hasSelectedSectionNotifications" class="d-flex justify-center position-relative mt-n1 mb-2" style="background: none">
        <div class="d-inline-flex bg-secondary rounded elevation-2 px-3">
          <v-btn-icon v-for="field in fieldFilters" :key="field.id" size="x-small" :rounded="false" :active="field.selected" @click="field.selected = ! field.selected">
            <span v-if="field.id == 'title'" style="font-family: Georgia, serif; font-size:20px">T</span>
            <v-icon v-else size="large">{{ field.icon }}</v-icon>
            <v-tooltip location="bottom" activator="parent">{{ field.label }}</v-tooltip>
          </v-btn-icon>
        </div>
      </v-list-subheader>
      <div v-if="! selectedSectionNotifications.length" class="pl-4 pb-2">
        {{ $t('NOTIFICATIONS.NOTIFICATIONS_COUNT', 0) }} <span v-if="selectedSection.section_type == 'planning' && ! hasSelectedSectionNotifications">{{ $t('NOTIFICATIONS.ON_THIS_PROJECT') }}</span>
      </div>
      <notifications-list :notifications="selectedSectionNotifications" :projects-map="projectsMap" :elements-map="elementsMap" @close="dropdownIsOpen = false"></notifications-list>
      <template v-if="planning && selectedSection.section_type == 'planning' && selectedSection.section_id == planning.id">
        <v-divider></v-divider>
        <navigation-notifications-planning-switch :planning="planning"></navigation-notifications-planning-switch>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
  import ProjectSrv from '@/components/Projects/ProjectSrv';
  import useNotificationsStore from '@/stores/notifications';
  import NotificationsList from '@/components/Notifications/NotificationsList.vue';
  import NavigationNotificationsPlanningSwitch from './NavigationNotificationsPlanningSwitch.vue';

  export default {
    components: {
      NotificationsList,
      NavigationNotificationsPlanningSwitch,
    },
    props: {
      planning: { type: Object, default: null },
    },
    data() {
      return {
        dropdownIsOpen: null,
        projectsMap: new Map(),
        selectedSection: null, // { section_type: 'planning', section_id: 123 } // can have count prop from unseenNotificationsCountBySection
        fieldFilters: [
          { id: 'newEl', icon: 'far fa-plus', label: this.$t('NOTIFICATIONS.NEW_ELEMENT'), selected: false },
          { id: 'title', label: this.$t('ELEMENT-DETAIL.TITLE'), selected: false },
          { id: 'html', icon: 'far fa-align-justify', label: this.$t('ELEMENT-DETAIL.DESCRIPTION'), selected: false },
          { id: 'date', icon: 'far fa-calendar-alt', label: this.$t('ELEMENT-DETAIL.DATES'), selected: false },
          { id: 'checklist', icon: 'far fa-tasks', label: this.$t('ELEMENT-DETAIL.CHECKLIST'), selected: false },
          { id: 'progress', icon: 'fas fa-long-arrow-right', label: this.$t('ELEMENT-DETAIL.PROGRESS'), selected: false },
          { id: 'user', icon: 'far fa-user', label: this.$t('ELEMENT-DETAIL.USERS'), selected: false },
          { id: 'link', icon: 'far fa-paperclip', label: this.$t('ELEMENT-DETAIL.ATTACHMENTS'), selected: false },
          { id: 'budget', icon: 'far fa-euro-sign', label: this.$t('ELEMENT-DETAIL.BUDGET'), selected: false },
          { id: 'comment', icon: 'far fa-comment-dots', label: this.$t('ELEMENT-DETAIL.COMMENTS'), selected: false },
        ],
        notificationsStore: useNotificationsStore(),
      };
    },
    computed: {
      unseenNotifications() {
        return this.notificationsStore.unseen;
      },
      unseenNotificationsCountBySection() {
        const bySection = this.unseenNotifications.reduce((acc, notif) => {
          if (! acc[`${notif.section_type}|${notif.section_id}`]) acc[`${notif.section_type}|${notif.section_id}`] = { section_type: notif.section_type, section_id: notif.section_id, count: 0 };
          acc[`${notif.section_type}|${notif.section_id}`].count++;
          return acc;
        }, {});
        return Object.values(bySection);
      },
      notifications() {
        return this.notificationsStore.all;
      },
      selectedFields() {
        const selectedFields = new Set();
        this.fieldFilters.forEach((field) => { if (field.selected) selectedFields.add(field.id); });
        return selectedFields.size ? selectedFields : null;
      },
      selectedSectionNotifications() {
        if (! this.selectedSection) return null;
        return this.notifications.filter((notif) => {
          if (notif.section_type != this.selectedSection.section_type || notif.section_id != this.selectedSection.section_id) return false; // filter section
          return ! this.selectedFields || notif.fields?.some(field => this.selectedFields.has(field)); // filter selected fields
        });
      },
      hasSelectedSectionNotifications() { // before filtering by fields
        return this.selectedSection && this.notifications.some(notif => notif.section_type == this.selectedSection.section_type && notif.section_id == this.selectedSection.section_id);
      },
      selectedSectionTitle() {
        if (this.selectedSection?.section_type == 'planning') {
          if (this.planning && this.selectedSection.section_id == this.planning.id) return this.planning.getTitle();
          return this.projectsMap.get(this.selectedSection.section_id)?.title || null;
        }
        return null;
      },
      hasUnseenNotifications() {
        return this.selectedSectionNotifications && this.selectedSectionNotifications.some(notif => ! notif.seen_at);
      },
      elementsMap() {
        const elementsMap = new Map();
        if (! this.planning) return elementsMap;
        this.planning.elements.forEach((el) => { elementsMap.set(`p${this.planning.id}e${el.id}`, el); });
        return elementsMap;
      },
    },
    watch: {
      dropdownIsOpen(newVal) {
        if (! newVal && this.planning) {
          // reset selectedSection on closing
          setTimeout(() => {
            if (! this.dropdownIsOpen) this.selectedSection = { section_type: 'planning', section_id: this.planning.id };
          }, 1000);
        }

        // load projects data on first open
        if (! newVal || this.projectsMap.size || ! this.unseenNotifications.length) return;
        ProjectSrv.list(true).then((projects) => {
          projects.forEach((project) => {
            this.projectsMap.set(project.id, project);
          });
        });
      },
      'planning.id': {
        handler(newVal) {
          if (newVal) this.selectedSection = { section_type: 'planning', section_id: this.planning.id };
        },
        immediate: true,
      },
    },
    mounted() {
      if (this.$route.query.open == 'notifications') {
        this.$router.replace({ query: { ...this.$route.query, open: undefined } });
        // this.dropdownIsOpen = true; // bug to fix : dropdown position
      }
    },
    methods: {
      notifSectionUrl(sectionNotif) {
        if (sectionNotif.section_type == 'planning') {
          const project = this.projectsMap.get(sectionNotif.section_id);
          if (! project) return `/planning/${sectionNotif.section_id}?open=notifications`;
          return `/${(project.access_right == 'consult' ? 'viewer/' : 'planning/') + project.id}/${window.slugify(project.title || "New project")}?open=notifications`;
        }
        return '/analysis/actions';
      },
      seenAllNotifications() {
        if (this.selectedFields) {
          this.notificationsStore.seen({ filters: { ids: this.selectedSectionNotifications.map(notif => notif.id) } });
          return;
        }
        this.notificationsStore.seen({ filters: this.selectedSection });
      },
    },
  };
</script>
